import { Link } from "react-router-dom";
import { CheckCircle2, ArrowUpRight } from "lucide-react";
import { PageHeader, Marquee, CTABand } from "../components/ui-blocks";
import { Reveal, Stagger, StaggerItem } from "../components/motion";
import { SERVICES, APPROACH } from "../data";

const DETAILS = {
  "executive-search": ["Confidential market mapping", "Passive candidate engagement", "Structured leadership assessment", "Offer & onboarding strategy"],
  "permanent": ["Culture & skills alignment", "Full-cycle sourcing", "Reference & background checks", "90-day placement guarantee"],
  "contract": ["Rapid deployment", "Compliance & timesheet management", "Contract-to-hire conversion", "Flexible scaling"],
  "rpo": ["Dedicated recruiting pod", "ATS & workflow integration", "Employer branding support", "Real-time hiring analytics"],
  "payroll": ["Multi-state compliance", "Employer of record (EOR)", "Benefits administration", "Accurate, on-time processing"],
  "technical": ["Domain-expert recruiters", "Specialized skill vetting", "Niche talent networks", "Clearance & certification checks"],
};

export default function Services() {
  return (
    <div data-testid="services-page">
      <PageHeader overline="What We Offer" title="Workforce solutions engineered for every stage" subtitle="Six ways we help you win the talent war — from a single executive hire to fully outsourced recruiting at scale." />
      <Marquee fast />

      <section className="py-20 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          {SERVICES.map((s, i) => (
            <Reveal key={s.key} delay={i * 0.05}>
              <div data-testid={`service-detail-${s.key}`} className="group grid grid-cols-1 lg:grid-cols-12 gap-8 rounded-2xl border border-slate-800/80 bg-[#111827]/70 p-8 sm:p-10 hover:border-amber-500/40 transition-all duration-300">
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-4 mb-5">
                    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-500/10 border border-amber-500/20 text-amber-400 group-hover:scale-110 transition-transform"><s.icon className="w-7 h-7" /></span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 border border-slate-700 rounded-full px-3 py-1">{s.tag}</span>
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-3">{s.title}</h2>
                  <p className="text-slate-400 leading-relaxed">{s.desc}</p>
                </div>
                <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3 self-center">
                  {DETAILS[s.key].map((d) => (
                    <div key={d} className="flex items-center gap-3 rounded-xl border border-slate-800/60 bg-[#0B0F19] px-4 py-3.5">
                      <CheckCircle2 className="w-5 h-5 text-amber-400 shrink-0" />
                      <span className="text-sm text-slate-300">{d}</span>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Approach */}
      <section className="py-20 sm:py-24 border-y border-slate-800/80 bg-[#0d1220]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="mb-14 max-w-3xl"><p className="overline mb-4">Our Process</p><h2 className="text-3xl sm:text-4xl font-bold text-white tracking-tight">A methodology built for retention</h2></Reveal>
          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {APPROACH.map((a, i) => (
              <StaggerItem key={a.step}>
                <div className="relative h-full rounded-2xl border border-slate-800/80 bg-[#111827]/70 p-7 hover:border-amber-500/30 transition-all">
                  <div className="font-mono text-xs text-amber-500 mb-4">STEP {String(i + 1).padStart(2, "0")}</div>
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400 mb-4"><a.icon className="w-6 h-6" /></span>
                  <h3 className="text-lg font-semibold text-white mb-2">{a.step}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{a.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.2} className="mt-10">
            <Link to="/employers" data-testid="services-cta" className="group inline-flex items-center gap-1.5 px-6 py-3.5 text-sm font-semibold text-slate-950 bg-gradient-to-r from-amber-400 to-amber-500 rounded-xl hover:shadow-lg hover:shadow-amber-500/30 transition-all">Start a Requirement <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></Link>
          </Reveal>
        </div>
      </section>

      <CTABand />
    </div>
  );
}
